import http from "@/lib/http/client"
import { Resource, ResourceField, SemanticType } from "@/types/api"

export const listResources = async (): Promise<Resource[]> => {
  const res = await http.get(`/resources`)
  return res.data
}

export const getResource = async (resourceId: string): Promise<Resource> => {
  const res = await http.get(`/resources/${resourceId}`)
  return res.data
}

export const createResource = async (data: {
  name: string
  description?: string
  type: string
  meta: any
}): Promise<Resource> => {
  const res = await http.post(`/resources`, data)
  return res.data
}

export const updateResource = async (
  resourceId: string,
  data: {
    name: string
    description?: string
    type: string
    meta: any
  }
): Promise<Resource> => {
  const res = await http.put(`/resources/${resourceId}`, data)
  return res.data
}

export const checkConnection = async (data: { resource_id: string }): Promise<{ success: boolean }> => {
  const res = await http.post(`/resources/check-connection/${data.resource_id}`)
  return res.data
}

export const validateConnection = async (data: { resource_type: string; meta: any }): Promise<{ success: boolean }> => {
  const res = await http.post(`/resources/validate-connection`, data)
  return res.data
}

export const listSchemas = async (data: { resource_type: string; meta: any }): Promise<{ schemas: string[] }> => {
  const res = await http.post(`/resources/list-schemas`, data)
  return res.data
}

export const listResourceSchemas = async (resourceId: string): Promise<{ schemas: string[] }> => {
  const res = await http.get(`/resources/${resourceId}/schemas`)
  return res.data
}

export const updateField = async (
  resourceId: string,
  containerId: string,
  fieldId: string,
  data: { description: string; display_name: string; visibility: string; data_type: string; semantic_type: string }
): Promise<void> => {
  const res = await http.put(`/resources/${resourceId}/containers/${containerId}/fields/${fieldId}`, data)
  return res.data
}

export const listContainers = async (resourceId: string): Promise<any[]> => {
  const res = await http.get(`/resources/${resourceId}/containers`)
  return res.data
}

export type ContainerWithFields = {
  id: string
  name: string
  meta?: {
    namespace?: string
  }
  fields: ResourceField[]
}

export const listContainersWithFields = async (resourceId: string): Promise<ContainerWithFields[]> => {
  const res = await http.get(`/resources/${resourceId}/containers-with-fields`)
  return res.data
}

export const listContainerFields = async (resourceId: string, containerId: string): Promise<any[]> => {
  const res = await http.get(`/resources/${resourceId}/containers/${containerId}/fields`)
  return res.data
}

export const getFieldPossibleValues = async (
  resourceId: string,
  containerId: string,
  fieldId: string
): Promise<any[]> => {
  const res = await http.get(`/resources/${resourceId}/containers/${containerId}/fields/${fieldId}/possible-values`)
  return res.data
}

export const updateFieldPossibleValues = async (
  resourceId: string,
  containerId: string,
  fieldId: string,
  possibleValues: any[]
): Promise<void> => {
  const res = await http.put(`/resources/${resourceId}/containers/${containerId}/fields/${fieldId}/possible-values`, {
    possible_values: possibleValues,
  })
  return res.data
}

export const generateContainerDescription = async (resourceId: string, containerId: string): Promise<string> => {
  const res = await http.get(`/resources/${resourceId}/containers/${containerId}/description`)
  return res.data
}

export const updateContainer = async (
  resourceId: string,
  containerId: string,
  data: { description: string }
): Promise<void> => {
  const res = await http.put(`/resources/${resourceId}/containers/${containerId}`, data)
  return res.data
}

export const resyncResource = async (resourceId: string): Promise<void> => {
  const res = await http.post(`/resources/${resourceId}/resync`)
  return res.data
}

export const listSemanticTypes = async (): Promise<SemanticType[]> => {
  const res = await http.get(`/resources/semantic-types`)
  return res.data
}

export const deleteResource = async (resourceId: string): Promise<void> => {
  const res = await http.delete(`/resources/${resourceId}`)
  return res.data
}
